<template>
    <skeleton v-if="courseData === undefined" />

    <template v-else>
        <waitlist-confirmation
            v-if="showWaitlistConfirmation"
            :confirmation-message="
                courseData.plan.bookingLimit.waitlistConfirmationMessage
            "
            :course-display-text="courseData.publicTitle"
            :course-dates-text="datesDisplay"
            :customers="confirmationWaitlistedCustomers"
            @back-to-home="handleBackToHome"
        />

        <template v-else>
            <content-reset
                v-if="courseData.plan.bookingLimit.waitlistJoinMessage !== ''"
                :body="courseData.plan.bookingLimit.waitlistJoinMessage"
            />

            <div class="mt-6 space-y-4">
                <div
                    class="flex flex-col items-start gap-4 sm:flex-row sm:gap-8"
                >
                    <div class="flex flex-col items-start">
                        <label
                            for="customersToAdd"
                            class="font-medium"
                        >
                            Participants
                        </label>
                        <input-number
                            id="customersToAdd"
                            v-model="customerCount"
                            show-buttons
                            button-layout="horizontal"
                            :min="1"
                            :input-props="{ inputmode: 'numeric' }"
                            input-class="w-12 text-center py-2 px-1 border-transparent ring-0 focus:outline-none focus:shadow-none hover:border-transparent dark:focus:shadow-none"
                            class="w-min border border-gray-300 focus-within:ring focus-within:ring-offset-1"
                            decrement-button-class="order-first"
                            :increment-button-props="{ text: true }"
                            :decrement-button-props="{ text: true }"
                            :pt="{
                                root: {
                                    class: 'w-auto inline-flex'
                                }
                            }"
                        >
                            <template #decrementbuttonicon>
                                <i class="pi pi-minus" />
                            </template>

                            <template #incrementbuttonicon>
                                <i class="pi pi-plus" />
                            </template>
                        </input-number>
                    </div>

                    <div>
                        <div
                            class="text-lg font-medium text-gray-600"
                            v-html="spacesRemainingDisplay"
                        />
                        <div v-if="courseData.subtitle !== ''">
                            {{ courseData.subtitle }}
                        </div>
                        <div
                            v-if="datesDisplay !== ''"
                            class="text-sm"
                        >
                            {{ datesDisplay }}
                        </div>
                    </div>
                </div>

                <div v-if="tooManyCustomersMessage !== null">
                    <prime-message
                        :closable="false"
                        severity="error"
                    >
                        {{ tooManyCustomersMessage }}
                    </prime-message>
                </div>

                <waitlist-enrollment-customer-picker
                    v-model:enrollment-inputs="enrollmentCustomersInput"
                    :course="courseData"
                    :collect-additional-comments="collectAdditionalComments"
                    :show-errors="showEnrollmentCustomersInputErrors"
                    :waitlist-constraints="waitlistConstraints"
                />

                <div class="flex items-center justify-end gap-4 pt-6">
                    <div
                        v-if="
                            showEnrollmentCustomersInputErrors &&
                            (tooManyCustomersMessage !== null ||
                                enrollmentCustomersInputInvalid)
                        "
                        class="text-red-600 font-medium"
                    >
                        Please fix the errors in red above
                    </div>

                    <prime-button
                        label="Join the Waitlist"
                        :loading="isPending"
                        @click="handleJoin"
                    />
                </div>
            </div>
        </template>
    </template>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const JoinWaitlistModalCourseFragment = graphql(/* GraphQL */ `
    fragment JoinWaitlistModalCourseFragment on Course {
        id
        courseFacilityHash
        waitlistSpacesRemaining
        publicTitle
        subtitle
        boundsStartLocal
        boundsEndLocal
        plan {
            bookingLimit(input: $input) {
                allowAdditionalComments
                showSpacesRemaining
                waitlistJoinMessage
                waitlistConfirmationMessage
            }
            terms {
                billingType
            }
        }
        ...WaitlistedCustomerConstraintPlanContractFragment
    }
`)
</script>

<script setup lang="ts">
import { computed, inject, onMounted, ref, watch } from 'vue'
import type {
    JoinWaitlistModalCourseFragmentFragment,
    AddToWaitlistInput,
    WaitlistEnrollmentSlotInput
} from '~/resources/graphql/graphql'
import { getFragmentData } from '~/resources/graphql'
import Skeleton from 'primevue/skeleton'
import PrimeButton from 'primevue/button'
import PrimeMessage from 'primevue/message'
import WaitlistEnrollmentCustomerPicker from '~/components/Waitlist/WaitlistEnrollmentCustomerPicker.vue'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { useRpQuery } from '~/composables/graphql'
import { useToast } from 'primevue/usetoast'
import { formatRange, parseLocal } from '~/composables/support/localization'
import InputNumber from 'primevue/inputnumber'
import {
    makeNullWaitlistEnrollmentInput,
    type WaitlistEnrollmentInputArray
} from '~/composables/wait-list-enrollment-input'
import WaitlistConfirmation from '~/components/Waitlist/WaitlistConfirmation.vue'
import ContentReset from '~/components/ContentReset.vue'
import { useWaitlistedCustomerConstraint } from '~/components/Waitlist/waitlisted-customer-constraint'

const dialogRef = inject('dialogRef')

const courseData = ref<JoinWaitlistModalCourseFragmentFragment>()
const customerCount = ref(1)

onMounted(() => {
    courseData.value = getFragmentData(
        JoinWaitlistModalCourseFragment,
        dialogRef.value.data.course
    )

    handleUpdateCustomerCount(customerCount.value, spacesRemaining.value)
})

const enrollmentCustomersInput = ref<WaitlistEnrollmentInputArray>([
    makeNullWaitlistEnrollmentInput()
])
const enrollmentCustomersInputInvalid = computed(() =>
    enrollmentCustomersInput.value.some(value => value.invalid)
)

const spacesRemaining = computed(() => {
    if (
        courseData.value === undefined ||
        courseData.value.waitlistSpacesRemaining == null
    ) {
        return 999999
    }

    return courseData.value.waitlistSpacesRemaining
})

const tooManyCustomersMessage = computed(() => {
    // If more customers than spaces remaining
    if (customerCount.value > spacesRemaining.value) {
        return (
            'There are only ' +
            spacesRemaining.value +
            ' spaces remaining on the waitlist.'
        )
    }

    return null
})

watch(customerCount, newCustomerCount => {
    handleUpdateCustomerCount(newCustomerCount, spacesRemaining.value)
})

watch(spacesRemaining, newSpacesRemaining => {
    handleUpdateCustomerCount(customerCount.value, newSpacesRemaining)
})

function handleUpdateCustomerCount(
    newCustomerCount: number,
    newSpacesRemaining: number
) {
    if (newCustomerCount > newSpacesRemaining) {
        // Warning will show, so we just return
        return
    }

    if (newCustomerCount > enrollmentCustomersInput.value.length) {
        enrollmentCustomersInput.value = [
            ...enrollmentCustomersInput.value,
            ...Array(newCustomerCount - enrollmentCustomersInput.value.length)
                .fill(null)
                .map(() => {
                    return makeNullWaitlistEnrollmentInput()
                })
        ]
    } else if (newCustomerCount < enrollmentCustomersInput.value.length) {
        enrollmentCustomersInput.value = enrollmentCustomersInput.value.slice(
            0,
            newCustomerCount
        )
    }
}

const query = useRpQuery()
const { mutate, isPending } = useMutation({
    mutationFn: async (mutationData: AddToWaitlistInput) => {
        const response = await query(
            graphql(/** @lang GraphQL */ `
                mutation AddToWaitlistModal($input: AddToWaitlistInput!) {
                    addToWaitlist(input: $input) {
                        __typename
                        ... on AddToWaitlistResult {
                            records {
                                ...WaitlistConfirmationWaitlistedCustomerFragment
                            }
                        }
                    }
                }
            `),
            {
                input: mutationData
            }
        )

        if (response.addToWaitlist.__typename !== 'AddToWaitlistResult') {
            throw new Error()
        }

        return response.addToWaitlist.records
    }
})

const showEnrollmentCustomersInputErrors = ref(false)
const toast = useToast()
const queryClient = useQueryClient()
const showWaitlistConfirmation = ref(false)
const confirmationWaitlistedCustomers = ref([])
function handleJoin() {
    if (enrollmentCustomersInputInvalid.value) {
        showEnrollmentCustomersInputErrors.value = true
        return
    }

    if (courseData.value === undefined) {
        throw new Error()
    }

    const enrollments: WaitlistEnrollmentSlotInput[] =
        enrollmentCustomersInput.value
            .filter(enrollment => enrollment.data !== null)
            .map(enrollment => {
                let additionalComments = null
                if (
                    collectAdditionalComments.value &&
                    enrollment.additionalComments !== ''
                ) {
                    additionalComments = enrollment.additionalComments
                }

                if (enrollment.data === null) {
                    throw new Error('enrollment.data is null')
                } else if (enrollment.data.type === 'existing') {
                    return {
                        customerId: enrollment.data.id,
                        additionalComments
                    }
                } else {
                    return {
                        newCustomer: {
                            firstName: enrollment.data.firstName,
                            lastName: enrollment.data.lastName,
                            middleName: enrollment.data.middleName,
                            linkCustomer: enrollment.data.householdMember,
                            dateOfBirth: enrollment.data.dateOfBirth
                        },
                        additionalComments
                    }
                }
            })

    mutate(
        {
            courseFacilityHash: courseData.value.courseFacilityHash,
            enrollments
        },
        {
            onSuccess: data => {
                toast.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Waitlist Joined',
                    life: 3000,
                    closable: false
                })
                queryClient.invalidateQueries({
                    predicate: query =>
                        query.queryKey.includes('CourseCalendarQuery')
                })
                showWaitlistConfirmation.value = true
                confirmationWaitlistedCustomers.value = data
            }
        }
    )
}

const datesDisplay = computed(() => {
    if (courseData.value === undefined) {
        throw new Error()
    }

    // Course bounds don't make sense for recurring program
    if (courseData.value.plan.terms.billingType === 'RECURRING') {
        return ''
    }

    return formatRange(
        parseLocal(courseData.value.boundsStartLocal),
        parseLocal(courseData.value.boundsEndLocal)
    )
})

const spacesRemainingDisplay = computed(() => {
    if (courseData.value === undefined) {
        return ''
    }

    if (!courseData.value.plan.bookingLimit.showSpacesRemaining) {
        return courseData.value.publicTitle
    }

    const spaces = courseData.value.waitlistSpacesRemaining
    if (spaces === null) {
        return courseData.value.publicTitle
    }

    const title =
        courseData.value.publicTitle !== ''
            ? courseData.value.publicTitle + ' &mdash; '
            : ''

    if (spaces === 1) {
        return title + '1 space remaining'
    } else {
        return title + spaces + ' spaces remaining'
    }
})

const collectAdditionalComments = computed(() => {
    if (courseData.value === undefined) {
        throw new Error()
    }

    return courseData.value.plan.bookingLimit.allowAdditionalComments
})

const waitlistConstraints = useWaitlistedCustomerConstraint(
    enrollmentCustomersInput,
    courseData
)

function handleBackToHome() {
    dialogRef.value.close()
}
</script>
